import { resolveRbacKeysForDataField } from './summaryRows';

import type { SummaryCardRow } from '../../../summary/summaryCardSourceTypes';
import type { SubsectionFieldDescriptor } from '../../../utils/subsectionHeaderMetrics';
import type { RequestFieldDefinition } from '../types';

export type EditorSubsectionSummaryBuildOptions = {
  editorFields: readonly SubsectionFieldDescriptor[];
  registryFields: readonly RequestFieldDefinition[];
  rbacAnchorDataField: string;
  /** Subsection heading marker on the first editor field (Racks pattern). */
  subsectionId?: string;
  labelOverrides?: Readonly<Record<string, string>>;
};

function resolveEditorSubsectionSummaryRbac(
  dataField: string,
  anchor: { permissionSection: string; permissionField: string },
): { permissionSection: string; permissionField: string } {
  return resolveRbacKeysForDataField(dataField) ?? anchor;
}

/**
 * Builds per-unit Summary rows in exact {@link SubsectionFieldDescriptor} editor order.
 * Registry labels/RBAC when present; editor short labels for instance identity fields.
 */
export function buildEditorSubsectionSummaryRows(
  options: EditorSubsectionSummaryBuildOptions,
): SummaryCardRow[] {
  const registryByField = new Map(
    options.registryFields.map(field => [field.dataField, field]),
  );
  const anchorRbac = resolveRbacKeysForDataField(options.rbacAnchorDataField);
  if (!anchorRbac) {
    throw new Error(
      `No fieldMapping entry for editor summary rbacAnchor=${options.rbacAnchorDataField}`,
    );
  }

  let subsectionMarked = false;

  return options.editorFields.map(({ key, shortLabel }) => {
    const registryField = registryByField.get(key);
    const rbac = resolveEditorSubsectionSummaryRbac(key, anchorRbac);

    let subsection: string | undefined;
    if (!subsectionMarked) {
      subsection = options.subsectionId ?? registryField?.subsectionId;
      subsectionMarked = true;
    }

    const label =
      options.labelOverrides?.[key] ?? registryField?.label ?? shortLabel;

    return {
      dataField: key,
      label,
      subsection,
      permissionSection: rbac.permissionSection,
      permissionField: rbac.permissionField,
    };
  });
}

export function editorSubsectionFieldKeys(
  fields: readonly SubsectionFieldDescriptor[],
): readonly string[] {
  return fields.map(field => field.key);
}

export function editorSubsectionFieldsExcluding(
  fields: readonly SubsectionFieldDescriptor[],
  excludedKeys: ReadonlySet<string>,
): SubsectionFieldDescriptor[] {
  return fields.filter(field => !excludedKeys.has(field.key));
}
